import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { DeliveryBoardStatus } from '@interfaces/ILogistics';
import { KebabButton } from '@components/ui/KebabButton/KebabButton';
import { Menu } from '@components/ui/Menu/Menu';
import { DiscardModal } from '@components/ui/DiscardModal/DiscardModal';
import { displayStatusName } from '../board.logic';
import { useInlineRename } from '../useInlineRename';
import styles from './DeliveryBoard.module.css';

/** Seeded defaults the backend refuses to delete — renamable but permanent.
 *  (They used to anchor an internal Genba status mapping; that mapping is gone,
 *  but the backend still guards them so a site cannot delete its way to an
 *  empty board.) 'delivered' is not in this set: it's `system`, which already
 *  drops the row's ⋮ entirely. */
const PERMANENT_STATUS_IDS: ReadonlySet<string> = new Set(['ordered', 'processing']);

/** Mirrors the backend's status-name cap (`NAME_MAX`, delivery-statuses
 *  service; `BoardStatusNameSchema`) — typed past it, a name only bounces. */
const STATUS_NAME_MAX = 40;

export interface IStatusesMenuProps {
  statuses: DeliveryBoardStatus[];
  countsById: Map<string, number>;
  /** Transient: a status write is in flight (or the page is locked). */
  busy: boolean;
  /** The caller's role may change statuses. Without it the panel only lists
   *  the columns and their counts: no add, rename or delete is rendered. */
  canEdit: boolean;
  onCreate: (name: string) => void;
  onRename: (statusId: string, name: string) => void;
  onDelete: (statusId: string) => void;
}

interface INameInputProps {
  initial?: string;
  busy: boolean;
  onCommit: (name: string) => void;
  onCancel: () => void;
}

/** Tiny inline editor shared by rename-in-place and "+ New status". The
 *  commit/cancel/Escape-stops-propagation mechanics live in
 *  `useInlineRename` (shared with the site-map area drawer's own rename
 *  field) — this only supplies the row's markup. */
function NameInput({ initial = '', busy, onCommit, onCancel }: INameInputProps) {
  const { t } = useTranslation();
  const { value, setValue, handleKeyDown, handleBlur } = useInlineRename({
    initial,
    onCommit,
    onCancel,
  });

  return (
    <input
      autoFocus
      type="text"
      className={styles.ddInput}
      aria-label={t('logistics.board.statusName')}
      value={value}
      maxLength={STATUS_NAME_MAX}
      disabled={busy}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
    />
  );
}

interface IRowMenuProps {
  status: DeliveryBoardStatus;
  count: number;
  busy: boolean;
  onRename: () => void;
  onDelete: () => void;
}

/** Per-row ⋮ — Rename / Delete, mirroring DeliveryPipeline's kebab menu. Delete
 *  is disabled (with a reason line) while the status still holds deliveries;
 *  base-ui's `Menu.Item` renders `aria-disabled="true"` for a disabled item
 *  (it's a non-native <div role="menuitem">, so it never gets the native
 *  `disabled` attribute) and swallows the click, so no render-prop workaround
 *  is needed here. A permanent default (`PERMANENT_STATUS_IDS`) drops the
 *  Delete item outright rather than showing it permanently disabled — the
 *  backend rejects the delete unconditionally, so there is no state in which
 *  clicking it would ever do anything. */
function RowMenu({ status, count, busy, onRename, onDelete }: IRowMenuProps) {
  const { t } = useTranslation();
  const blocked = count > 0;
  const deletable = !PERMANENT_STATUS_IDS.has(status.id);
  return (
    <Menu.Root>
      <Menu.Trigger
        render={
          <KebabButton
            className={styles.ddKebab}
            aria-label={t('logistics.board.statusSettings', { name: displayStatusName(status, t) })}
            disabled={busy}
          />
        }
      />
      <Menu.Portal>
        <Menu.Positioner side="bottom" align="end" sideOffset={4}>
          <Menu.Popup>
            <Menu.Item onClick={onRename}>{t('logistics.board.rename')}</Menu.Item>
            {deletable ? (
              <Menu.Item className={styles.deleteItem} disabled={blocked} onClick={onDelete}>
                {t('logistics.board.delete')}
                {blocked ? (
                  <span className={styles.menuWhy}>{t('logistics.board.deleteBlocked', { count })}</span>
                ) : null}
              </Menu.Item>
            ) : null}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}

/**
 * Board-header settings control: a kebab trigger opening a one-item base-ui
 * `Menu` ("Manage statuses"), which in turn opens a custom popover — not a
 * base-ui `Menu` itself — listing every column with its live card count,
 * per-row rename/delete, and an inline "+ New status" composer.
 *
 * The management panel is custom rather than a base-ui `Menu.Popup` on
 * purpose — the rename/add composers are `<input>`s that need to keep focus
 * and take keystrokes while the panel stays open, which a base-ui menu (built
 * to close on interaction and manage its own roving focus) actively fights.
 * The per-row ⋮ underneath still gets to be a real `Menu` since it has no
 * such input inside it, and so does the outer kebab — it's a single static
 * item with no input either.
 */
export function StatusesMenu({ statuses, countsById, busy, canEdit, onCreate, onRename, onDelete }: IStatusesMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  /** The status whose Delete was picked, awaiting the confirm modal. */
  const [confirming, setConfirming] = useState<DeliveryBoardStatus | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  /** Where the panel sits: pinned under the trigger, in viewport terms. */
  const [panelPosition, setPanelPosition] = useState<CSSProperties>({});

  /* The panel renders on <body>: the board card clips its overflow, and a
     search can shrink the board to a sliver that would cut the panel off. So
     it follows its trigger itself — on open, and on any scroll or resize. */
  useLayoutEffect(() => {
    if (!open) return;
    const root = rootRef.current;
    if (!root) return;
    const pinPanelToTrigger = () => {
      const triggerRect = root.getBoundingClientRect();
      const top = triggerRect.bottom + 4;
      const right = window.innerWidth - triggerRect.right;
      // Scrolls anywhere fire this: keep the same object when nothing moved.
      setPanelPosition((prev) => (prev.top === top && prev.right === right ? prev : { top, right }));
    };
    pinPanelToTrigger();
    window.addEventListener('scroll', pinPanelToTrigger, true);
    window.addEventListener('resize', pinPanelToTrigger);
    return () => {
      window.removeEventListener('scroll', pinPanelToTrigger, true);
      window.removeEventListener('resize', pinPanelToTrigger);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // A row's ⋮ is a real base-ui `Menu`, which portals its popup onto
    // `document.body` — outside `rootRef` in the DOM, even though it is
    // logically "inside" this dropdown. Both listeners below treat a click or
    // Escape landing inside an open `role="menu"` popup as belonging to that
    // inner menu, not as dismissing this outer panel; base-ui's own Menu
    // already closes itself on outside click / Escape / item selection. The
    // delete confirm modal portals the same way, and gets the same pass.
    const insideRowMenu = (target: EventTarget | null) =>
      target instanceof Element && target.closest('[role="menu"], [role="dialog"]') !== null;

    const onDown = (e: MouseEvent) => {
      const target = e.target;
      if (!(target instanceof Node)) return;
      if (rootRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      if (insideRowMenu(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (insideRowMenu(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className={styles.statusesRoot} ref={rootRef}>
      <Menu.Root>
        <Menu.Trigger render={<KebabButton aria-label={t('logistics.board.settings')} />} />
        <Menu.Portal>
          <Menu.Positioner side="bottom" align="end" sideOffset={4}>
            <Menu.Popup>
              <Menu.Item onClick={() => setOpen(true)}>{t('logistics.board.manageStatuses')}</Menu.Item>
            </Menu.Popup>
          </Menu.Positioner>
        </Menu.Portal>
      </Menu.Root>
      {open ? createPortal(
        <div className={styles.dropdownPanel} ref={panelRef} style={panelPosition}>
          <div className={styles.ddTitle}>{t('logistics.board.boardStatuses')}</div>
          {statuses.map((status) => {
            const count = countsById.get(status.id) ?? 0;
            return (
              <div key={status.id} className={styles.ddRow} data-row={status.id}>
                {renaming === status.id ? (
                  <NameInput
                    /* The name the row SHOWS — an unrenamed default reads
                       translated, and its stored seed string was never on
                       screen. Committed untouched it is no rename: storing
                       the translation would stop the default translating. */
                    initial={displayStatusName(status, t)}
                    busy={busy}
                    onCommit={(name) => {
                      if (name !== displayStatusName(status, t)) onRename(status.id, name);
                      setRenaming(null);
                    }}
                    onCancel={() => setRenaming(null)}
                  />
                ) : (
                  <span className={styles.ddName}>{displayStatusName(status, t)}</span>
                )}
                <span className={styles.ddCount}>{count}</span>
                {canEdit && !status.system ? (
                  <RowMenu
                    status={status}
                    count={count}
                    busy={busy}
                    onRename={() => setRenaming(status.id)}
                    onDelete={() => setConfirming(status)}
                  />
                ) : null}
              </div>
            );
          })}
          {!canEdit ? null : adding ? (
            <div className={styles.ddRow}>
              <NameInput
                busy={busy}
                onCommit={(name) => {
                  onCreate(name);
                  setAdding(false);
                }}
                onCancel={() => setAdding(false)}
              />
            </div>
          ) : (
            <button type="button" className={styles.ddAdd} disabled={busy} onClick={() => setAdding(true)}>
              {t('logistics.board.newStatus')}
            </button>
          )}
        </div>,
        document.body,
      ) : null}
      {confirming ? (
        <DiscardModal
          open
          onCancel={() => setConfirming(null)}
          onConfirm={() => {
            onDelete(confirming.id);
            setConfirming(null);
          }}
          title={t('logistics.board.confirmDeleteStatus.title', {
            name: displayStatusName(confirming, t),
          })}
          description={t('logistics.board.confirmDeleteStatus.body')}
        />
      ) : null}
    </div>
  );
}
